import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Button } from '../ui/button';
import { X, Upload, Trash2, Image as ImageIcon, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = '';

/**
 * ProofEnvoiModal - Modal pour uploader/voir la preuve d'envoi (ACHAT tickets)
 */
export const ProofEnvoiModal = ({ isOpen, onClose, ticket, onProofUpdated }) => {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // Check if ticket has preuve_envoi
  const hasExistingProof = ticket?.preuve_envoi && ticket.preuve_envoi !== '' && !ticket.preuve_envoi.startsWith('http');
  const existingProofIsUrl = ticket?.preuve_envoi && (ticket.preuve_envoi.startsWith('http') || ticket.preuve_envoi.startsWith('/uploads'));

  // Reset state when modal opens/closes or ticket changes
  useEffect(() => {
    if (!isOpen) {
      setFile(null);
      setPreviewUrl(null);
    }
  }, [isOpen, ticket?.id]);

  const handleFileSelect = useCallback((selectedFile) => {
    if (!selectedFile) return;
    
    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(selectedFile.type)) {
      toast.error('Format non supporté. Utilisez PNG, JPG ou WEBP.');
      return;
    }
    
    // Validate file size (5MB max)
    if (selectedFile.size > 5 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 5MB)');
      return;
    }
    
    setFile(selectedFile);
    setPreviewUrl(URL.createObjectURL(selectedFile));
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleUpload = async () => {
    if (!file || !ticket) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API_URL}/api/tickets/${ticket.id}/upload-preuve-envoi`, {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Erreur upload');
      }
      
      const data = await response.json();
      toast.success('Preuve d\'envoi uploadée !');
      
      if (onProofUpdated) {
        onProofUpdated(ticket.id, data.preuve_envoi, 'envoi');
      }
      
      onClose();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(`Erreur: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!ticket) return;
    
    if (!window.confirm('Supprimer cette preuve d\'envoi ?')) return;
    
    setDeleting(true);
    try {
      const response = await fetch(`${API_URL}/api/tickets/${ticket.id}/preuve-envoi`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error('Erreur suppression');
      }
      
      toast.success('Preuve d\'envoi supprimée');
      
      if (onProofUpdated) {
        onProofUpdated(ticket.id, null, 'envoi');
      }
      
      onClose();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  if (!isOpen || !ticket) return null;

  // Build display URL
  let displayUrl = null;
  if (existingProofIsUrl) {
    if (ticket.preuve_envoi.startsWith('http')) {
      displayUrl = ticket.preuve_envoi;
    } else {
      displayUrl = `${API_URL}${ticket.preuve_envoi}`;
    }
  }

  return (
    <div 
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className="bg-zinc-900 border border-zinc-700 rounded-lg max-w-lg w-full p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">
            Preuve d'envoi
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-zinc-700 rounded transition-colors"
          >
            <X className="h-5 w-5 text-zinc-400" />
          </button>
        </div>

        {/* Ticket info */}
        <div className="bg-zinc-800/50 rounded-lg p-3 mb-4">
          <p className="text-xs text-zinc-400 mb-1">TICKET ACHAT</p>
          <p className="text-sm text-white font-medium truncate">{ticket.evenement}</p>
          <p className="text-xs text-zinc-400 mt-1">Compte: {ticket.email_compte}</p>
        </div>

        {/* Existing proof view */}
        {existingProofIsUrl && displayUrl && !previewUrl && (
          <div className="mb-4">
            <p className="text-xs text-zinc-400 mb-2">Preuve d'envoi actuelle</p>
            <div className="relative aspect-video bg-zinc-800 rounded-lg overflow-hidden">
              <img
                src={displayUrl}
                alt="Preuve d'envoi"
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `<div class="flex flex-col items-center justify-center h-full text-zinc-500">
                    <span class="text-sm">Image non disponible</span>
                    <a href="${displayUrl}" target="_blank" class="text-blue-400 text-xs mt-2 hover:underline">Ouvrir le lien</a>
                  </div>`;
                }}
              />
            </div>
            <div className="flex gap-2 mt-3">
              <Button
                size="sm"
                variant="outline"
                onClick={() => window.open(displayUrl, '_blank')}
                className="flex-1 border-zinc-600"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Ouvrir
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleDelete}
                disabled={deleting}
                className="text-red-400 border-red-500/40 hover:bg-red-500/20"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                {deleting ? 'Suppression...' : 'Supprimer'}
              </Button>
            </div>
          </div>
        )}

        {/* Upload area */}
        {(!existingProofIsUrl || previewUrl) && (
          <div
            ref={dropZoneRef}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current?.click()}
            className={`
              border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
              transition-colors
              ${previewUrl 
                ? 'border-green-500/50 bg-green-500/10' 
                : 'border-zinc-600 hover:border-[#C4A052] hover:bg-zinc-800/50'
              }
            `}
          >
            {previewUrl ? (
              <div className="space-y-3">
                <img
                  src={previewUrl}
                  alt="Prévisualisation"
                  className="max-h-48 mx-auto rounded-lg"
                />
                <p className="text-sm text-green-400">
                  {file?.name}
                </p>
                <p className="text-xs text-zinc-400">
                  Cliquez pour changer de fichier
                </p>
              </div>
            ) : (
              <>
                <Upload className="h-10 w-10 mx-auto text-zinc-500 mb-3" />
                <p className="text-zinc-300 mb-1">
                  Glissez une image ou cliquez pour sélectionner
                </p>
                <p className="text-xs text-zinc-500">
                  PNG, JPG, WEBP - Max 5MB
                </p>
              </>
            )}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/webp"
          onChange={(e) => handleFileSelect(e.target.files[0])}
          className="hidden"
        />

        {/* Actions */}
        {previewUrl && (
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setFile(null);
                setPreviewUrl(null);
              }}
              className="flex-1 border-zinc-600"
            >
              Annuler
            </Button>
            <Button
              onClick={handleUpload}
              disabled={uploading}
              className="flex-1 bg-[#C4A052] hover:bg-[#D4B86A] text-black font-semibold"
            >
              {uploading ? 'Upload...' : 'Uploader'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProofEnvoiModal;
